import { useTranslations } from '@vocab/react';
import {
  Actions,
  Button,
  RadioGroup,
  RadioItem,
  Stack,
} from 'braid-design-system';
import type { FormEvent } from 'react';

import { TaxonomyOptionsErrorAlert } from 'src/components/Profile/TaxonomyOptionsErrorAlert/TaxonomyOptionsErrorAlert';
import { AVAILABILITY } from 'src/components/Router';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';

import {
  useAvailability,
  useAvailabilityOptions,
  useUpdateAvailability,
} from '../data';

import translations from './.vocab';

export const EditAvailability = () => {
  const { t } = useTranslations(translations);
  const { language } = useAppConfig();
  const { availability } = useAvailability();
  const {
    availabilityOptions,
    error: availabilityOptionsError,
    loading,
    refetch: refetchAvailabilityOptions,
  } = useAvailabilityOptions();
  const enterReadMode = useEnterReadMode();

  const [updateAvailability] = useUpdateAvailability();

  const handleOnChange = ({
    currentTarget: { value },
  }: FormEvent<HTMLInputElement>) => {
    updateAvailability({
      availability: { id: Number(value) },
      languageCode: language,
    });
  };

  const availabilityRef = useFocus<HTMLInputElement>({
    card: AVAILABILITY,
    fieldName: 'availability',
    domReady: !loading && !availabilityOptionsError,
  });

  const mapSelectionToRadioItem = (
    item: { id: number; description: string },
    index: number,
  ) => (
    <RadioItem
      label={item.description}
      value={item.id}
      key={index}
      data={{
        automation: `availability-${index}`,
      }}
      {...(index === 0 && { ref: availabilityRef })}
    />
  );

  return availabilityOptionsError ? (
    <TaxonomyOptionsErrorAlert refetch={refetchAvailabilityOptions} />
  ) : (
    <Stack space="large">
      <RadioGroup
        id="availability-selections"
        aria-label={t('Select availability')}
        value={Number(availability?.id) ?? ''}
        onChange={handleOnChange}
        data={{
          automation: 'availability-selections',
        }}
      >
        {availabilityOptions.map(mapSelectionToRadioItem)}
      </RadioGroup>
      <Actions>
        <Button
          id="availability-done-btn"
          tone="formAccent"
          data={{ automation: 'availability-done' }}
          onClick={enterReadMode}
        >
          {t('Done')}
        </Button>
      </Actions>
    </Stack>
  );
};
